/**
 * Session Expiry Helpers
 * Utilities for handling session expiry logic in camp hierarchy
 */

/**
 * Get the current session year (default for new sessions)
 * @returns {number} Current year (e.g., 2024, 2025)
 */
export function getCurrentSessionYear() {
  return new Date().getFullYear();
}

/**
 * Calculate session expiry date (end of session year + 3 months)
 * @param {number} sessionYear - Year the session applies to
 * @returns {string} Expiry date in YYYY-MM-DD format
 */
export function calculateSessionExpiry(sessionYear) {
  if (!sessionYear) {
    return null;
  }
  
  // End of session year is December 31st
  // Add 3 months = March 31st of the following year
  const expiryYear = sessionYear + 1;
  const expiryDate = `${expiryYear}-03-31`;
  return expiryDate;
}

/**
 * Check if a session is expired
 * @param {Object} camp - Camp object with session_expires_at field
 * @returns {boolean} True if session is expired, false otherwise
 */
export function isSessionExpired(camp) {
  if (!camp) {
    return false;
  }
  
  // Check both top-level and program_data for session_expires_at
  const expiresAt = camp.session_expires_at || camp.program_data?.session_expires_at;
  if (!expiresAt) {
    // If no expiry date, treat as non-expiring (backward compatibility)
    return false;
  }
  
  const expiryDate = new Date(expiresAt);
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Compare dates only, ignore time
  
  return expiryDate < today;
}

/**
 * Get expired sessions from an array of camps
 * @param {Array} camps - Array of camp objects
 * @returns {Array} Array of camps with expired sessions
 */
export function getExpiredSessions(camps) {
  if (!Array.isArray(camps)) {
    return [];
  }
  
  return camps.filter(camp => {
    // Check both top-level and program_data for session
    const session = camp.session || camp.program_data?.session || '';
    return session && isSessionExpired(camp);
  });
}

/**
 * Check if session should be shown (exists and is not expired)
 * @param {Object} camp - Camp object with session and session_expires_at fields
 * @returns {boolean} True if session should be shown, false otherwise
 */
export function shouldShowSession(camp) {
  if (!camp) {
    return false;
  }
  
  // Check both top-level and program_data for session field
  const session = camp.session || camp.program_data?.session || '';
  if (!session) {
    return false;
  }
  
  return !isSessionExpired(camp);
}

/**
 * Get session year from camp object
 * @param {Object} camp - Camp object with session_year field
 * @returns {number} Session year, defaults to current year if missing
 */
export function getSessionYear(camp) {
  if (!camp) {
    return getCurrentSessionYear();
  }
  
  // Check both top-level and program_data for session_year
  return camp.session_year || camp.program_data?.session_year || getCurrentSessionYear();
}
